import React, { useEffect, useRef, useState } from "react";
import { usd, usdSigned, StaticNote } from "./ui";
import { fetchExecutive, saveExecNote, fetchBoardReportBlob } from "../api";
import Recruitment from "./Recruitment";
import Census from "./Census";
import "./Dashboard.css";

const NOTE_OWNER = "peterabraldes@nursepartners.net";

const fmt = (n) => {
  if (n === null || n === undefined) return "--";
  const s = "$" + Math.abs(Math.round(n)).toLocaleString("en-US");
  return n < 0 ? `(${s})` : s;
};

const ADDBACKS = [
  ["owner_ltc", "Owner LTC Insurance"],
  ["owner_wages", "Owner Wages"],
  ["owner_401k", "Owner 401(k) Match"],
  ["health_dental", "Health & Dental Insurance"],
  ["auto", "Auto Expense"],
  ["transportation", "Transportation"],
  ["depreciation", "Depreciation"],
  ["legal_prof", "Legal & Professional Fees"],
  ["admin_software", "Admin Software (7%)"],
  ["offshore", "Offshore Professional Services"],
  ["ceo_tech", "CEO Tech Consulting"],
];

function WideTable({ title, cols, rows }) {
  return (
    <section className="panel">
      <div className="panel-head"><h2>{title}</h2></div>
      <div className="pl-scroll">
        <table className="grid pl-table">
          <thead><tr><th>Line Item</th>{cols.map((c, i) => <th key={c} className={i === cols.length - 1 ? "strong" : ""}>{c}</th>)}</tr></thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.label} className={r.type === "total" ? "total-row" : r.highlight ? "row-highlight" : ""}>
                <td className={"rowhead" + (r.indent ? " indent" : "")}>{r.label}</td>
                {r.vals.map((v, i) => <td key={i} className={v < 0 ? "neg-text" : ""}>{fmt(v)}</td>)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export default function Executive({ ctx }) {
  const [pass, setPass] = useState("");
  const [exec, setExec] = useState(null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [noteSaving, setNoteSaving] = useState(false);
  const [noteMsg, setNoteMsg] = useState(null);
  const [view, setView] = useState("financials");
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfErr, setPdfErr] = useState(null);
  const noteRef = useRef(null);

  const email = (ctx.account?.username || ctx.account?.idTokenClaims?.email || "").toLowerCase();
  const canEditNote = email === NOTE_OWNER;

  const unlock = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try {
      const data = await fetchExecutive(ctx, pass);
      setExec(data);
      setNote(data.analysisNote || "");
    } catch (e2) { setErr(e2.status === 403 ? "Incorrect passcode." : e2.message); }
    finally { setBusy(false); }
  };

  // Grow the note box to fit its content so nothing is hidden behind a scrollbar.
  const fitNote = () => {
    const el = noteRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${Math.max(el.scrollHeight, 180)}px`;
  };
  useEffect(fitNote, [note, view, exec]);

  const downloadBoardReport = async () => {
    setPdfBusy(true); setPdfErr(null);
    try {
      const { blob, filename } = await fetchBoardReportBlob(ctx, pass);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e2) { setPdfErr(e2.message || "Could not build the board report."); }
    finally { setPdfBusy(false); }
  };

  const saveNote = async () => {
    setNoteSaving(true); setNoteMsg(null);
    try {
      const r = await saveExecNote(ctx, pass, note);
      setExec({ ...exec, analysisNote: r.analysisNote });
      setNoteMsg("Saved");
    } catch (e2) { setNoteMsg(e2.message || "Save failed"); }
    finally { setNoteSaving(false); }
  };

  if (!exec) {
    return (
      <div className="dash">
        <section className="panel exec-lock">
          <div className="lock-icon">🔒</div>
          <h2>Executive — protected</h2>
          <p>Enter the Executive passcode to view normalized EBITDA and owner add-backs.</p>
          <form onSubmit={unlock} className="lock-form">
            <input type="password" value={pass} onChange={(e) => setPass(e.target.value)} placeholder="Passcode" autoFocus />
            <button className="btn" type="submit" disabled={busy || !pass}>{busy ? "Checking…" : "Unlock"}</button>
          </form>
          {err && <div className="lock-err">{err}</div>}
        </section>
      </div>
    );
  }

  const months = exec.months;
  const cols = [...months.map((m) => m.label), "Total"];
  const t = exec.totals;
  const series = (fn, total) => [...months.map(fn), total];

  const incomeRows = [
    { label: "Total Revenue", vals: series((m) => m.total_income, t.total_income), type: "total" },
    { label: "Total COGS", vals: series((m) => m.total_cogs, t.total_cogs), indent: true },
    { label: "Gross Profit", vals: series((m) => m.gross_profit, t.gross_profit), highlight: true },
    { label: "Total Expenses", vals: series((m) => m.total_expenses, t.total_expenses), indent: true },
    { label: "Net Profit / Loss", vals: series((m) => m.net_profit, t.net_profit), type: "total" },
  ];

  const addbackRows = ADDBACKS.map(([key, label]) => {
    const vals = months.map((m) => m.addbacks?.[key] ?? 0);
    const total = vals.reduce((s, v) => s + v, 0);
    return { label, vals: [...vals, total], indent: true, _total: total };
  }).filter((r) => r._total > 0);
  addbackRows.push({ label: "Total Add-Backs", vals: series((m) => m.total_addbacks, t.total_addbacks), type: "total" });

  const bridgeRows = [
    { label: "Net Profit / Loss", vals: series((m) => m.net_profit, t.net_profit), indent: true },
    { label: "Total Add-Backs", vals: series((m) => m.total_addbacks, t.total_addbacks), indent: true },
    { label: "Normalized EBITDA", vals: series((m) => m.normalized_ebitda, t.normalized_ebitda), type: "total", highlight: true },
  ];

  return (
    <div className="dash">
      <div>
        <h1 className="pl-title">NursePartners Inc. — Executive Dashboard</h1>
      </div>
      <div className="subnav subnav-row">
        <div className="subnav-tabs">
          <button className={view === "financials" ? "on" : ""} onClick={() => setView("financials")}>Financials</button>
          <button className={view === "recruitment" ? "on" : ""} onClick={() => setView("recruitment")}>Recruitment</button>
          <button className={view === "census" ? "on" : ""} onClick={() => setView("census")}>Census</button>
        </div>
        <button className="btn btn-download" onClick={downloadBoardReport} disabled={pdfBusy}
          title="Financials, Recruitment and Census in one PDF to send to the board">
          {pdfBusy ? "Building PDF…" : "Download all three (PDF)"}
        </button>
      </div>
      {pdfErr && <div className="lock-err">{pdfErr}</div>}

      {view === "recruitment" && <Recruitment ctx={ctx} passcode={pass} />}
      {view === "census" && <Census ctx={ctx} passcode={pass} />}

      {view === "financials" && (
      <>
        <p className="pl-sub">Rolling 12-Month Actuals: {exec.periodLabel} &nbsp;|&nbsp; Normalized EBITDA with Owner Add-Backs</p>

      <section className="kpis kpis-3">
        <div className="kpi">
          <div className="kpi-label">Net profit / loss</div>
          <div className={"kpi-value " + (t.net_profit >= 0 ? "pos-text" : "neg-text")}>{usdSigned(t.net_profit)}</div>
          <div className="kpi-sub">{exec.periodLabel}</div>
        </div>
        <div className="kpi">
          <div className="kpi-label">Total add-backs</div>
          <div className="kpi-value">{usd(t.total_addbacks)}</div>
        </div>
        <div className="kpi kpi-primary">
          <div className="kpi-label">Normalized EBITDA</div>
          <div className="kpi-value">{usd(t.normalized_ebitda)}</div>
        </div>
      </section>

      {exec.commentary && (
        <section className="panel exec-context">
          <div className="panel-head"><h2>{exec.commentary.title}</h2><span>latest finalized month</span></div>
          {exec.commentary.headline && <p className="exec-headline">{exec.commentary.headline}</p>}

          {exec.commentary.variance?.length > 0 && (
            <div className="pl-scroll">
              <table className="grid" style={{ marginBottom: 16 }}>
                <thead><tr><th>Line</th><th>Actual</th><th>Budget</th><th>Variance</th></tr></thead>
                <tbody>
                  {exec.commentary.variance.map((v, i) => {
                    const va = (v.actual != null && v.budget != null) ? v.actual - v.budget : null;
                    return (
                      <tr key={i}>
                        <td className="rowhead">{v.line}</td>
                        <td>{fmt(v.actual)}</td>
                        <td>{fmt(v.budget)}</td>
                        <td className={va == null ? "muted" : va >= 0 ? "pos-text" : "neg-text"}>{va != null ? usdSigned(va) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {(exec.commentary.drivers?.length > 0 || exec.commentary.recommendations?.length > 0) && (
            <div className="exec-cols">
              {exec.commentary.drivers?.length > 0 && (
                <div>
                  <h3 className="exec-sub">Key drivers</h3>
                  <ul className="exec-drivers">
                    {exec.commentary.drivers.map((d, i) => (
                      <li key={i}><span className="amt neg-text">{d.amount}</span><span><b>{d.name}</b> <span className="muted">— {d.note}</span></span></li>
                    ))}
                  </ul>
                </div>
              )}
              {exec.commentary.recommendations?.length > 0 && (
                <div>
                  <h3 className="exec-sub">Recommendations for next month</h3>
                  <ol className="exec-recs">
                    {exec.commentary.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                  </ol>
                </div>
              )}
            </div>
          )}

          {exec.commentary.outlook && <div className="exec-outlook"><b>Outlook.</b> {exec.commentary.outlook}</div>}

          {canEditNote ? (
            <div className="exec-note-edit">
              <h3 className="exec-sub">Additional details {noteMsg && <span className="note-flag">· {noteMsg}</span>}</h3>
              <textarea className="exec-note-ta" value={note} ref={noteRef}
                onChange={(e) => { setNote(e.target.value); setNoteMsg(null); }}
                placeholder="Add any last-minute details or context for this month's analysis…" />
              <button className="btn" onClick={saveNote} disabled={noteSaving}>{noteSaving ? "Saving…" : "Save details"}</button>
            </div>
          ) : (exec.analysisNote ? (
            <div className="exec-note-view">
              <h3 className="exec-sub">Additional details</h3>
              <p>{exec.analysisNote}</p>
            </div>
          ) : null)}
        </section>
      )}

      <WideTable title="Income Statement" cols={cols} rows={incomeRows} />
      <WideTable title="Normalized Add-Backs" cols={cols} rows={addbackRows} />
      <WideTable title="Normalized EBITDA Bridge" cols={cols} rows={bridgeRows} />

      {exec.adjustments?.length > 0 && (
        <section className="panel">
          <div className="panel-head"><h2>Expense Adjustments</h2><span>reflected in the P&amp;L</span></div>
          <table className="grid">
            <thead><tr><th>Item</th><th>Reason</th><th>Effective</th><th>Amount</th><th>Basis</th></tr></thead>
            <tbody>
              {exec.adjustments.map((a, i) => (
                <tr key={i}>
                  <td className="rowhead">{a.item}</td>
                  <td className="muted">{a.reason}{a.detail ? ` (${a.detail})` : ""}</td>
                  <td>{a.effective}</td>
                  <td className="strong">{a.recurring ? `${usd(a.monthly)}/mo` : usd(a.amount)}</td>
                  <td>{a.recurring ? "Recurring" : "One-time"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}

      <StaticNote>Executive figures are QuickBooks actuals ported from the legacy app (rolling 12 months). A live QuickBooks Online integration is planned.</StaticNote>
      </>
      )}
    </div>
  );
}
